import React, { useState, useEffect, useRef } from "react";
import { Box, Spinner, useBreakpointValue, useMediaQuery, useTheme, Flex } from "@chakra-ui/react";
import { colors } from "../../constants/GlobalStyles";
import {
  PAGE_HEADER_HEIGHT,
  PAGE_HEADER_HEIGHT_MOBILE,
  wisdoliaJungleUrl,
  MIN_WIDTH_OF_TABLET_PORTAIT_VIEW,
  MAX_WIDTH_OF_TABLET_PORTAIT_VIEW,
  EventSentToMobileApp,
  UserSubscriptionPlan,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import {
  askQuestionsChatStore,
  cardStore,
  pageStore,
  userStore,
  useStreakStore,
  useSubscriptionUpsellStore,
  useGlobalWebsiteStore,
} from "../../zustand/globalStore";
import { useLocation } from "react-router-dom";
import NavigationBar from "../NavigationBar";
import { useTutorHidesPhoneTabBar } from "../../features/tutor/integration/tutorPhoneTabBar";
import { useTutorUiEnabled } from "../../features/tutor/integration/tutorAvailability";
import {
  getCurrentStreakLength,
  getStripeManageAccountSessionForUser,
  getUserSubscriptionPlanAndLimits,
  trackEvent,
} from "../../backend/utils/helper-methods";
import DeferredGlobalModals from "./DeferredGlobalModals";
import {
  LOCAL_ELITE_FIRST_UPSELL_PREVIEW_CONTEXT,
  LOCAL_SIDE_BY_SIDE_UPSELL_PREVIEW_CONTEXT,
} from "../upsell/upsellFlowConfig";
import { useHelperMethodsHook } from "../../contextsAndHooks/HelperMethodsHook";
import { startPaywallJourneyReplay } from "../../utils/analytics/paywallReplayLifecycle";
import { getLatestPaywallTraceProperties } from "../../utils/analytics/paywallTraceStorage";
import { createSubscriptionRequestAuthority, SubscriptionRequestCancelledError } from "../../utils/subscription-request-authority";
import { trackError } from "../../utils/analytics/track-error";
import { LazySidePanelWithTabsComponent, loadedSidePanel } from "../side-panel-component/sidePanelLoader";
import SidePanelShell from "../side-panel-component/SidePanelShell";

// The panel renders straight away when its code is already loaded (a surface warmed it), so the
// click never paints a loading frame; otherwise the shell stands in until the code arrives.
function SidePanelSlot(props: React.ComponentProps<typeof LazySidePanelWithTabsComponent>) {
  const [Panel] = useState(() => loadedSidePanel() ?? LazySidePanelWithTabsComponent);
  return <Panel {...props} />;
}

const PREDICTIVE_GENERATIONS_REMAINING_THRESHOLD = 5;
const PREDICTIVE_CHAT_MESSAGES_REMAINING_THRESHOLD = 2;
const PREDICTIVE_ANSWER_FEEDBACKS_REMAINING_THRESHOLD = 2;
const PREDICTIVE_IMAGE_OCCLUSIONS_REMAINING_THRESHOLD = 1;
const PREDICTIVE_LIVE_RECORDINGS_REMAINING_THRESHOLD = 1;
// Marks a page a learner browses, as opposed to a screen that takes the whole window for one task (see
// where they are used below)
const BROWSING_PAGE_CLASS_NAME = "jungle-browsing-page";
const BROWSING_PAGE_ATTRIBUTE = "data-jungle-browsing-page";
// Signing in is one task too. Most of its pages do not use this wrapper; the one that does (forgot
// password) must stand on the same ground as the rest of that family.
const SIGN_IN_ROUTES = ["/forgot-password", "/login", "/signup"];

interface PageWrapperProps {
  children: React.ReactNode;
}

function PageWrapper({ children }: PageWrapperProps) {
  const tutorUiEnabled = useTutorUiEnabled();
  const isRightToLeft = useTheme().direction === "rtl";
  // redux state
  const user = userStore((state) => state.currentUser);
  const { sendEventsToMobileApp } = useHelperMethodsHook();

  // zustand states
  const { title, headerType, showJungleBackground } = pageStore((state) => state.pageSetup);
  const { isNavigationBarCollapsed } = useGlobalWebsiteStore((state) => state);
  const { isAskQuestionsChatModalOpened, chatSidePanelParameters } = askQuestionsChatStore((state) => state);
  const { isRunningAsMobileApp } = pageStore((state) => state);
  const { currentStreakLength, setCurrentStreakLength } = useStreakStore((state) => state);
  const { isHeaderFixed, setIsHeaderFixed } = pageStore((state) => state);
  const { shouldShowSubscriptionUpsellModal, showSubscriptionUpsellModal } = useSubscriptionUpsellStore(
    (state) => state
  );
  const { selectedCards } = cardStore((state) => state);
  const showCardEditingPage = cardStore((state) => state.showCardEditingPage);
  const { isInGenerateQuestionsSubScreen } = useGlobalWebsiteStore((state) => state);

  // local states
  const [isLoading, setIsLoading] = useState(false);
  const [isSupportSubscriptionLoading, setIsSupportSubscriptionLoading] = useState(false);
  const supportSubscriptionOperation = useRef<object | null>(null);
  const [reloadChildrenContent, setReloadChildrenContent] = useState(false);

  //Variables
  const SWIPE_THRESHOLD = 200;
  const location = useLocation();
  const hasOpenedTwoStepUpsellPreviewRef = useRef(false);
  const hasHandledSupportSubscriptionActionRef = useRef(false);
  const lastPredictivePaywallSignalKeyRef = useRef<string | null>(null);
  const [isMobile] = useMediaQuery("(max-width: 767px)");
  // A class chat, the full-screen search and a call take the whole phone screen (the tutor tab's list keeps the bar).
  const tutorHidesPhoneTabBar = useTutorHidesPhoneTabBar();
  const [isOnTabletPortraitView] = useMediaQuery(
    `(min-width: ${MIN_WIDTH_OF_TABLET_PORTAIT_VIEW}px) and (max-width: ${MAX_WIDTH_OF_TABLET_PORTAIT_VIEW}px)`
  );
  const { userDecks } = userStore((state) => state);
  const shouldShowFirstTimeGeneratingHomeView =
    !user ||
    (user &&
      (!user?.number_of_sets_of_cards_generated || user?.number_of_sets_of_cards_generated === 0) &&
      (!user?.number_of_questions_answered || user?.number_of_questions_answered < 1) &&
      (!user?.class_properties?.class_decks_id || user?.class_properties?.class_decks_id?.length === 0) &&
      userDecks?.length < 2);
  // DEV-only preview hook for the subscription upsell modal:
  //   ?open_two_step_upsell_preview=1            → opens the mounted flow as the viewer would see it
  //                                                 (two-step: step 1, the commit page; elite-first:
  //                                                 the viewer's paywall-format assignment)
  //   ?open_two_step_upsell_preview=plan_picker  → two-step flow: opens directly on step 2
  //   ?open_two_step_upsell_preview=side_by_side → elite-first flow's side-by-side
  //                                                 Pro + Elite screen, for any viewer
  //   ?open_two_step_upsell_preview=elite_first  → elite-first flow's Elite offer → Pro downsell
  //                                                 sequence, for any viewer
  // The two forced formats stay out of the paywall-format experiment (no exposure).
  const localPreviewSearchParams = new URLSearchParams(location.search);
  const twoStepUpsellPreviewParam = import.meta.env.DEV
    ? localPreviewSearchParams.get("open_two_step_upsell_preview")
    : null;
  const supportSubscriptionAction = localPreviewSearchParams.get("support_action");

  // Navigation configuration - using excluded routes approach
  const navigationConfig = {
    // Routes where navigation is hidden (shared baseline for both platforms)
    excludedRoutes: ["/review-deck", "/review-decks", "/learning-journey-overview", "/recording/", "/summary-outline", "/study-guide"],
    // Platform-specific overrides
    overrides: {
      desktop: {
        // Routes to force-include on desktop even if in excludedRoutes
        forceInclude: [
          // Example: "/review-deck/analytics" - would show nav on desktop but not mobile
        ],
        // Additional routes to exclude only on desktop
        additionalExclude: [
          // Example: "/mobile-only-feature"
        ],
      },
      mobile: {
        // Routes to force-include on mobile even if in excludedRoutes
        forceInclude: [
          // Example: "/review-deck/summary" - would show nav on mobile but not desktop
        ],
        // Additional routes to exclude only on mobile
        additionalExclude: ["/edit-card"],
      },
    },
  };

  // Common conditions that hide navigation on both platforms
  const commonNavigationHideConditions =
    (shouldShowFirstTimeGeneratingHomeView && !(user && tutorUiEnabled)) ||
    isInGenerateQuestionsSubScreen ||
    (selectedCards?.length > 0 && location.pathname.startsWith("/review-deck/"));

  // Check if route is excluded based on platform
  const isRouteExcluded = (platform: "desktop" | "mobile"): boolean => {
    const currentPath = location.pathname;
    const overrides = navigationConfig.overrides[platform];

    // Check if route is force-included for this platform
    const isForceIncluded = overrides.forceInclude.some((route) => currentPath.startsWith(route));
    if (isForceIncluded) return false;

    // Check if route is in base excluded routes
    const isInBaseExcluded = navigationConfig.excludedRoutes.some((route) => currentPath.startsWith(route));

    // Check if route is in platform-specific additional excludes
    const isInAdditionalExclude = overrides.additionalExclude.some((route) => currentPath.startsWith(route));

    return isInBaseExcluded || isInAdditionalExclude;
  };

  // Desktop-specific hide conditions
  const desktopSpecificHideConditions = location.pathname.startsWith("/folder/") && isOnTabletPortraitView;

  // Determine navigation bar visibility
  const shouldShowNavigationBar = isMobile
    ? !isRouteExcluded("mobile") && !commonNavigationHideConditions && !tutorHidesPhoneTabBar
    : !isRouteExcluded("desktop") && !commonNavigationHideConditions && !desktopSpecificHideConditions;

  // In the dark themes a page a learner browses (home, a class, a set, a list of questions, a form,
  // settings) lies on a deeper ground than a screen that takes the whole window for one task (a review, a
  // recording, the steps of a generation): the class on the box below, and "pages a learner browses" in
  // src/theme/jungleThemeColors.css. It follows the route, not whether the sidebar happens to show: a
  // shared set opened without the sidebar is the same page as that set with it. The list of all questions
  // and the question editor are opened on a review's route but are a list and a form.
  const isOnAWholeWindowTaskRoute = navigationConfig.excludedRoutes.some((route) =>
    location.pathname.startsWith(route)
  );
  const isShowingAListOrAFormOnATaskRoute =
    localPreviewSearchParams.get("showListView") !== null || showCardEditingPage;
  const isOnASignInRoute = SIGN_IN_ROUTES.some((route) => location.pathname.startsWith(route));
  const isBrowsingPage =
    !(isOnAWholeWindowTaskRoute && !isShowingAListOrAFormOnATaskRoute) &&
    !isInGenerateQuestionsSubScreen &&
    !isOnASignInRoute;
  // The attribute on <html> is for the strip of the document a phone shows when the page is pulled past
  // its end.
  useEffect(() => {
    if (!isBrowsingPage) return undefined;
    document.documentElement.setAttribute(BROWSING_PAGE_ATTRIBUTE, "");
    return () => document.documentElement.removeAttribute(BROWSING_PAGE_ATTRIBUTE);
  }, [isBrowsingPage]);

  // this is for swipe down to refresh for mobile app
  let touchStartYScrolling = null;

  useEffect(() => {
    if (title) {
      document.title = title;
    }
  }, [title]);

  // Add effect to get streak data
  useEffect(() => {
    const getStreakData = async () => {
      if (!user?.id) return;

      try {
        const streakLength = await getCurrentStreakLength(user.id);
        setCurrentStreakLength(streakLength);
      } catch (error) {
        console.error("Error getting streak data:", error);
      }
    };
    if (isRunningAsMobileApp || window.ReactNativeWebView) {
      sendEventsToMobileApp(EventSentToMobileApp.LOADED_PAGE);
    }
    if (user && !currentStreakLength) {
      getStreakData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [user?.id]);

  const backgroundProperties = useBreakpointValue({
    lg: showJungleBackground
      ? {
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundImage: `url('${wisdoliaJungleUrl}')`,
        }
      : {},
  });
  // The photograph is a pale watercolor that fades to white, which is the light page. A dark theme has to
  // repaint it to make it fade into a dark page (src/theme/jungleThemeArtwork.css), and its rule can only
  // name the picture through a variable. The light theme does not read the variable.
  const firstVisitPhotoBackgroundImage = (backgroundProperties as { backgroundImage?: string } | undefined)
    ?.backgroundImage;
  // The page's column is centered in what the sidebar leaves free (the padding on the box below), so its
  // middle is half the sidebar's width toward the reading end. The dark themes fade the photograph
  // around that middle. The photograph only shows on wide windows, where the padding applies.
  const widthReservedForNavigationBarInPixels =
    user && shouldShowNavigationBar ? (isNavigationBarCollapsed ? 148 : 292) : 0;
  const firstVisitPhotoColumnCenter = `calc(50% ${isRightToLeft ? "-" : "+"} ${widthReservedForNavigationBarInPixels / 2}px)`;
  // this is for swipe down to refresh for mobile app
  const handleTouchStartOfScrolling = (event) => {
    if (event.touches && event.touches.length > 0 && isRunningAsMobileApp) {
      const { pageY } = event.touches[0];
      touchStartYScrolling = pageY;
    } else {
      touchStartYScrolling = null;
    }
  };

  // this is for swipe down to refresh for mobile app
  const handleTouchEndOfScrolling = (event) => {
    if (window.scrollY > 10) return;
    if (event.changedTouches && event.changedTouches.length > 0 && isRunningAsMobileApp) {
      const { pageY } = event.changedTouches[0];

      if (touchStartYScrolling === null) return;
      const deltaY = pageY - touchStartYScrolling;
      if (deltaY > SWIPE_THRESHOLD) {
        setIsLoading(true);
        setReloadChildrenContent(true);
        // set children content to reload after 10ms
        setTimeout(() => {
          setReloadChildrenContent(false);
        }, 10);
        // show spinner for 1 second
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      }
    }
  };

  React.useEffect(() => {
    const headerElement = document.querySelector("#pageHeader");
    if (headerElement) {
      // putting this in setTimeout because it takes some time for the position to get updated
      setTimeout(() => {
        const computedStyle = window.getComputedStyle(headerElement);
        const isFixed = computedStyle.position === "fixed";

        setIsHeaderFixed(isFixed);
      }, 2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [headerType]);

  useEffect(() => {
    if (!twoStepUpsellPreviewParam) return;
    if (shouldShowSubscriptionUpsellModal) return;
    if (hasOpenedTwoStepUpsellPreviewRef.current) return;

    hasOpenedTwoStepUpsellPreviewRef.current = true;
    showSubscriptionUpsellModal({
      contextForShowingSubscriptionUpsell:
        twoStepUpsellPreviewParam === "side_by_side"
          ? LOCAL_SIDE_BY_SIDE_UPSELL_PREVIEW_CONTEXT
          : twoStepUpsellPreviewParam === "elite_first"
            ? LOCAL_ELITE_FIRST_UPSELL_PREVIEW_CONTEXT
            : "local two-step upsell preview",
      initialStep: twoStepUpsellPreviewParam === "plan_picker" ? "plan_picker" : "commit_to_subscribe",
    });
  }, [twoStepUpsellPreviewParam, shouldShowSubscriptionUpsellModal, showSubscriptionUpsellModal]);

  useEffect(() => {
    if (!user?.id || hasHandledSupportSubscriptionActionRef.current) return;
    if (
      supportSubscriptionAction !== "open_subscription_plans" &&
      supportSubscriptionAction !== "manage_subscription"
    ) {
      return;
    }

    hasHandledSupportSubscriptionActionRef.current = true;
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("support_action");
    window.history.replaceState({}, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);

    if (supportSubscriptionAction === "open_subscription_plans") {
      void showSubscriptionUpsellModal({
        contextForShowingSubscriptionUpsell: "customer support subscription link",
        initialStep: "plan_picker",
      });
      return;
    }

    let active = true;
    const operation = {};
    supportSubscriptionOperation.current = operation;
    setIsSupportSubscriptionLoading(true);
    const finishLoading = () => {
      if (active && supportSubscriptionOperation.current === operation) {
        supportSubscriptionOperation.current = null;
        setIsSupportSubscriptionLoading(false);
      }
    };
    const authority = createSubscriptionRequestAuthority(user, () => active, finishLoading);
    // Account management includes payment methods; a plan-change deep link hides those controls.
    void getStripeManageAccountSessionForUser(user, false, authority.isCurrent).then((billingPortalUrl) => {
      if (authority.isCurrent()) window.location.assign(billingPortalUrl);
    }).catch((error) => {
      if (!authority.isCurrent() || error instanceof SubscriptionRequestCancelledError) return;
      trackError(error, { scope: "support-subscription-link", userId: user.id });
    }).finally(() => {
      finishLoading();
      authority.dispose();
    });
    return () => {
      finishLoading();
      active = false;
      authority.dispose();
    };
    // Unrelated profile snapshots must not cancel an existing link action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportSubscriptionAction, showSubscriptionUpsellModal, user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    const {
      userSubscriptionPlan,
      userNumOfMonthlyGenerationsLeft,
      userNumOfChatMessagesRemaining,
      userNumOfAnswerFeedbacksRemaining,
      userNumOfImageOcclusionUsesLeft,
      userNumOfMonthlyLiveRecordingsLeft,
    } = getUserSubscriptionPlanAndLimits(user);

    // Subscribers on either paid tier have nothing to be predictively paywalled about.
    if (
      userSubscriptionPlan === UserSubscriptionPlan.SUPER_LEARNER ||
      userSubscriptionPlan === UserSubscriptionPlan.ELITE
    )
      return;

    const predictivePaywallSignals = [
      {
        signal: "monthly_generations_low",
        context: "user is close to their monthly generation limit",
        source: location.pathname.startsWith("/generate-questions")
          ? "generate_questions_route"
          : "global_quota_watcher",
        remaining: userNumOfMonthlyGenerationsLeft,
        threshold: PREDICTIVE_GENERATIONS_REMAINING_THRESHOLD,
      },
      {
        signal: "chat_messages_low",
        context: "user is close to their chat message limit",
        source: isAskQuestionsChatModalOpened ? "ask_questions_chat_open" : "global_quota_watcher",
        remaining: userNumOfChatMessagesRemaining,
        threshold: PREDICTIVE_CHAT_MESSAGES_REMAINING_THRESHOLD,
      },
      {
        signal: "answer_feedbacks_low",
        context: "user is close to their answer feedback limit",
        source: location.pathname.startsWith("/review-deck") ? "review_deck_route" : "global_quota_watcher",
        remaining: userNumOfAnswerFeedbacksRemaining,
        threshold: PREDICTIVE_ANSWER_FEEDBACKS_REMAINING_THRESHOLD,
      },
      {
        signal: "image_occlusions_low",
        context: "user is close to their image occlusion limit",
        source: location.pathname.startsWith("/generate-questions")
          ? "generate_questions_route"
          : "global_quota_watcher",
        remaining: userNumOfImageOcclusionUsesLeft,
        threshold: PREDICTIVE_IMAGE_OCCLUSIONS_REMAINING_THRESHOLD,
      },
      {
        signal: "live_recordings_low",
        context: "user is close to their live recording limit",
        source: location.pathname.startsWith("/recording") ? "live_recording_route" : "global_quota_watcher",
        remaining: userNumOfMonthlyLiveRecordingsLeft,
        threshold: PREDICTIVE_LIVE_RECORDINGS_REMAINING_THRESHOLD,
      },
    ];

    const strongestPredictivePaywallSignal = predictivePaywallSignals
      .filter(({ remaining, threshold }) => typeof remaining === "number" && remaining <= threshold)
      .sort((firstSignal, secondSignal) => firstSignal.remaining - secondSignal.remaining)[0];

    if (!strongestPredictivePaywallSignal) return;

    const predictivePaywallSignalKey = [
      user.id,
      strongestPredictivePaywallSignal.signal,
      strongestPredictivePaywallSignal.source,
      strongestPredictivePaywallSignal.remaining,
      location.pathname,
    ].join(":");

    if (lastPredictivePaywallSignalKeyRef.current === predictivePaywallSignalKey) return;
    lastPredictivePaywallSignalKeyRef.current = predictivePaywallSignalKey;

    const startPredictivePaywallReplay = async () => {
      const existingPaywallTraceProperties = getLatestPaywallTraceProperties();
      const paywallTraceProperties = await startPaywallJourneyReplay({
        replayStartReason: "predictive_signal",
        predictiveReplaySignal: strongestPredictivePaywallSignal.signal,
        predictiveReplaySignalSource: strongestPredictivePaywallSignal.source,
        context: strongestPredictivePaywallSignal.context,
      });

      const predictivePaywallTrackingProperties = {
        ...paywallTraceProperties,
        predictiveReplaySignal: strongestPredictivePaywallSignal.signal,
        predictiveReplaySignalSource: strongestPredictivePaywallSignal.source,
        predictiveReplayRemaining: strongestPredictivePaywallSignal.remaining,
        predictiveReplayThreshold: strongestPredictivePaywallSignal.threshold,
        userNumOfMonthlyGenerationsLeft,
        userNumOfChatMessagesRemaining,
        userNumOfAnswerFeedbacksRemaining,
        userNumOfImageOcclusionUsesLeft,
        userNumOfMonthlyLiveRecordingsLeft,
        pathname: location.pathname,
        search: location.search,
      };

      if (!existingPaywallTraceProperties.predictiveReplayStartedAt) {
        trackEvent("Predictive Paywall Replay Started", predictivePaywallTrackingProperties, user);
      }
    };

    void startPredictivePaywallReplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [isAskQuestionsChatModalOpened, location.pathname, location.search, user?.id, user?.subscription_information]);

  // Helper function to determine margin top

  return (
    <Box
      className={isBrowsingPage ? BROWSING_PAGE_CLASS_NAME : undefined}
      background={colors.surface.offWhite}
      display="flex"
      flexDirection="column"
      overflow="visible"
      minH="100svh"
    >
      {/* =============================
       Global Modal Section
       =============================
      This section contains global modals that can be triggered from anywhere in the application. 
      */}
      <React.Suspense fallback={isAskQuestionsChatModalOpened ? <SidePanelShell parameters={chatSidePanelParameters} /> : null}>
        {isAskQuestionsChatModalOpened && <SidePanelSlot {...chatSidePanelParameters} />}
      </React.Suspense>
      <DeferredGlobalModals />

      <Flex direction="row" flex="1">
        {shouldShowNavigationBar &&
          !isMobile &&
          user &&
          (tutorUiEnabled || user?.number_of_sets_of_cards_generated > 0 || user?.number_of_questions_answered > 0) && (
            <Box position="fixed" insetInlineStart={0} zIndex={1000}>
              <NavigationBar />
            </Box>
          )}

        <Box
          className={firstVisitPhotoBackgroundImage
            ? `jungle-first-visit-photo${shouldShowFirstTimeGeneratingHomeView && location.pathname === "/" ? " jungle-first-visit-photo--panel" : ""}`
            : undefined}
          style={
            {
              // How much of the window's reading start the navigation takes on this page (0 where it does not
              // show). A bar fixed to the bottom of the window starts there, not under the navigation
              // (QuestionSideNavigatorComponent).
              "--jungle-navigation-bar-width": `${widthReservedForNavigationBarInPixels}px`,
              ...(firstVisitPhotoBackgroundImage
                ? {
                    "--jungle-art-first-visit-photo": firstVisitPhotoBackgroundImage,
                    "--jungle-art-first-visit-photo-column-center": firstVisitPhotoColumnCenter,
                  }
                : {}),
            } as React.CSSProperties
          }
          width="100%"
          minHeight={
            isHeaderFixed ? `calc(100svh - ${isMobile ? PAGE_HEADER_HEIGHT_MOBILE : PAGE_HEADER_HEIGHT})` : "100svh"
          }
          paddingBottom={shouldShowNavigationBar && isMobile ? "80px" : "0"}
          {...backgroundProperties}
          onTouchStart={handleTouchStartOfScrolling}
          onTouchEnd={handleTouchEndOfScrolling}
          paddingInlineStart={{
            md: user && shouldShowNavigationBar ? (isNavigationBarCollapsed ? "148px" : "292px") : "0px",
          }}
        >
          {(isLoading || isSupportSubscriptionLoading) && (
            <Box display="flex" justifyContent="center" paddingTop="10px">
              <Spinner color={colors.text.label} size="sm" />
            </Box>
          )}
          {reloadChildrenContent ? <></> : <>{children}</>}
        </Box>
      </Flex>
      {shouldShowNavigationBar && isMobile && user && <NavigationBar />}
    </Box>
  );
}
export default React.memo(PageWrapper);
